import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import PortalAxios from "../../service/portalAxios";
import { extractContact, extractNameAndAddress } from "../../utils";
import {
  ClientBasicInfo,
  ClientContactInfo,
  ClientInfo,
  ClientPartnerState,
} from "../../types/portal";

const initialState: ClientPartnerState = {
  client: {
    basicInfo: null,
    contactInfo: null,
  },
  partner: {
    basicInfo: null,
    contactInfo: null,
  },
  loading: false,
  error: null,
};
export const api = `${process.env.REACT_APP_BASE_URL}client/client-details`;
export const fetchClientAndPartner = createAsyncThunk(
  "clientPartner/fetchClientAndPartner",
  async (clientId: string, { rejectWithValue }) => {
    try {
      // Fetch client data in parallel
      const [nameAddressRes, contactRes] = await Promise.all([
        PortalAxios.get(`${api}/${clientId}/name_and_address`),
        PortalAxios.get(`${api}/${clientId}/contact`),
      ]);

      // Extract the first item from the data array
      const clientNameAddress = nameAddressRes.data.data[0] || {};
      const clientContact = contactRes.data.data[0] || {};

      // Extract client info
      const clientBasic = extractNameAndAddress(clientNameAddress);
      const clientContactInfo = extractContact(clientContact);

      let partnerBasic: ClientBasicInfo | null = null;
      let partnerContact: ClientContactInfo | null = null;

      // Fetch partner data if exists
      if (clientBasic.AssociatedWith) {
        try {
          const [partnerNameRes, partnerContactRes] = await Promise.all([
            PortalAxios.get(
              `${api}/${clientBasic.AssociatedWith}/name_and_address`
            ),
            PortalAxios.get(`${api}/${clientBasic.AssociatedWith}/contact`),
          ]);

          if (!partnerNameRes.data.isError && !partnerContactRes.data.isError) {
            const partnerNameData = partnerNameRes.data.data[0] || {};
            const partnerContactData = partnerContactRes.data.data[0] || {};

            partnerBasic = extractNameAndAddress(partnerNameData);
            partnerContact = extractContact(partnerContactData);
          }
        } catch (error) {
          console.warn("Partner data fetch failed", error);
          // Return client data even if partner fetch fails
          return {
            client: {
              basicInfo: clientBasic,
              contactInfo: clientContactInfo,
            },
            partner: {
              basicInfo: null,
              contactInfo: null,
            },
          };
        }
      }

      return {
        client: {
          basicInfo: clientBasic,
          contactInfo: clientContactInfo,
        },
        partner: {
          basicInfo: partnerBasic,
          contactInfo: partnerContact,
        },
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error.message || "An unexpected error occurred"
      );
    }
  }
);

export const fetchPartnerData = createAsyncThunk(
  "clientPartner/fetchPartnerData",
  async (partnerId: string, { rejectWithValue }) => {
    try {
      const [nameAddressRes, contactRes] = await Promise.all([
        PortalAxios.get(
          `${process.env.REACT_APP_BASE_URL}client/client-details/${partnerId}/name_and_address`
        ),
        PortalAxios.get(
          `${process.env.REACT_APP_BASE_URL}client/client-details/${partnerId}/contact`
        ),
      ]);

      if (nameAddressRes.data.isError || contactRes.data.isError) {
        return rejectWithValue("Failed to fetch partner details");
      }

      const partnerNameData = nameAddressRes.data.data[0] || {};
      const partnerContactData = contactRes.data.data[0] || {};

      return {
        basicInfo: extractNameAndAddress(partnerNameData),
        contactInfo: extractContact(partnerContactData),
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch partner data"
      );
    }
  }
);

const clientPartnerSlice = createSlice({
  name: "clientPartner",
  initialState,
  reducers: {
    resetClientPartner: () => initialState,
    updateClientEmail: (state, action: PayloadAction<string>) => {
      if (state.client.contactInfo) {
        state.client.contactInfo.Email = action.payload;
      }
    },
    updatePartnerEmail: (state, action: PayloadAction<string>) => {
      if (state.partner.contactInfo) {
        state.partner.contactInfo.Email = action.payload;
      }
    },
    updateAssociatedWith: (state, action: PayloadAction<string | null>) => {
      if (state.client.basicInfo) {
        state.client.basicInfo.AssociatedWith = action.payload;
      }
    },
    setClientActiveStatus: (state, action: PayloadAction<boolean>) => {
      if (state.client.basicInfo) {
        state.client.basicInfo.ActiveOnPortal = action.payload;
      }
    },
    setPartnerActiveStatus: (state, action: PayloadAction<boolean>) => {
      if (state.partner.basicInfo) {
        state.partner.basicInfo.ActiveOnPortal = action.payload;
      }
    },
    setPartnerData: (state, action: PayloadAction<ClientInfo | null>) => {
      state.partner = action.payload || initialState.partner;
    },
    updatePartnerData: (state, action: PayloadAction<Partial<ClientInfo>>) => {
      if (action.payload.basicInfo) {
        state.partner.basicInfo = {
          ...(state.partner.basicInfo || {}),
          ...action.payload.basicInfo,
        } as ClientBasicInfo;
      }
      if (action.payload.contactInfo) {
        state.partner.contactInfo = {
          ...(state.partner.contactInfo || {}),
          ...action.payload.contactInfo,
        } as ClientContactInfo;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientAndPartner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientAndPartner.fulfilled, (state, action) => {
        state.loading = false;
        state.client = action.payload.client;
        state.partner = action.payload.partner;
      })
      .addCase(fetchClientAndPartner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.client = initialState.client;
        state.partner = initialState.partner;
      })
      .addCase(fetchPartnerData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPartnerData.fulfilled, (state, action) => {
        state.loading = false;
        state.partner = action.payload;
      })
      .addCase(fetchPartnerData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  resetClientPartner,
  updateClientEmail,
  updatePartnerEmail,
  updateAssociatedWith,
  setClientActiveStatus,
  setPartnerActiveStatus,
  setPartnerData,
  updatePartnerData,
} = clientPartnerSlice.actions;
export default clientPartnerSlice.reducer;
